import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Box, Breadcrumbs, Link, Tooltip, Typography } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink, To } from "react-router-dom";

export interface AppBreadcrumbItem {
    label: ReactNode;
    secondaryLabel?: ReactNode;
    icon?: ReactNode;
    to?: To;
}

interface AppBreadcrumbsProps {
    items: AppBreadcrumbItem[];
}

const BreadcrumbLabel = ({ item, current }: { item: AppBreadcrumbItem; current: boolean }) => (
    <Box sx={{
        minWidth: 0,
        maxWidth: { xs: 160, sm: 240 },
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        borderBottom: current ? "2px solid" : "2px solid transparent",
        borderColor: current ? "primary.main" : "transparent",
        pb: 0,
    }}>
        {item.icon && (
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    color: current ? "primary.main" : "text.secondary",
                    flexShrink: 0,
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                }}
            >
                {item.icon}
            </Box>
        )}
        <Typography
            component="span"
            variant="body2"
            color={current ? "text.primary" : "text.secondary"}
            fontWeight={500}
            noWrap
        >
            {item.label}
        </Typography>
    </Box>
);

export const AppBreadcrumbs = ({ items }: AppBreadcrumbsProps) => (
    <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNextIcon fontSize="small" />}
        maxItems={4}
        sx={{
            mb: 0.25,
            width: "fit-content",
            maxWidth: "100%",
            "& .MuiBreadcrumbs-li": { minWidth: 0 },
        }}
    >
        {items.map((item, index) => {
            const isCurrent = index === items.length - 1;
            const label = (
                <Tooltip title={item.secondaryLabel ?? ""} arrow placement="top">
                    <span>
                        <BreadcrumbLabel item={item} current={isCurrent} />
                    </span>
                </Tooltip>
            );

            if (!isCurrent && item.to) {
                return (
                    <Link
                        key={index}
                        component={RouterLink}
                        to={item.to}
                        color="inherit"
                        underline="hover"
                        sx={{ minWidth: 0 }}
                    >
                        {label}
                    </Link>
                );
            }

            return <Box key={index} component="span">{label}</Box>;
        })}
    </Breadcrumbs>
);